import { useCallback, useEffect, useState } from 'react'
import { HomeOutlined, ReloadOutlined } from '@ant-design/icons'
import { Button, Card, Popconfirm, Typography } from 'antd'
import { useHistory } from 'react-router'
import api from '../../api/index.api'
import { CardChart, ListPageIntro, ListPageShell } from '../../common'
import { onGetFeedbacks } from '../../redux/feedback/feedback.action'
import { onGetUsers } from '../../redux/user/user.action'
import { onGetWords } from '../../redux/word/word.action'
import { onSendNotifications } from '../../redux/order/order.action'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { ReportWordLangPieCharts } from '../ReportList/ReportWordLangPieCharts'

import { ROUTE } from '../../utils/constant'
import { formatDecimal } from '../../utils/function.utils'
import './Dashboard.scss'
import { onGetPercentWordDontHaveDefinitionWithInterval } from 'src/redux/statistic/statistic.action'

const { Text } = Typography

export const DashboardPage = () => {
	const history = useHistory()
	const dispatch = useAppDispatch()

	const user = useAppSelector(state => state.user)
	const word = useAppSelector(state => state.word)
	const feedback = useAppSelector(state => state.feedback)
	const statistic = useAppSelector(state => state.statistic)

	const [isReloadLoading, setIsReloadLoading] = useState()
	const [reportStats, setReportStats] = useState(null)
	const [reportStatsLoading, setReportStatsLoading] = useState(false)

	const fetchReportStats = useCallback(async () => {
		setReportStatsLoading(true)
		try {
			const data = await api.reportApi.getReportStats({})
			setReportStats(data)
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(e)
			setReportStats(null)
		} finally {
			setReportStatsLoading(false)
		}
	}, [])

	useEffect(() => {
		dispatch(onGetPercentWordDontHaveDefinitionWithInterval('month'))
	}, [dispatch])

	useEffect(() => {
		dispatch(onGetUsers())
		dispatch(onGetWords())
		dispatch(onGetFeedbacks())
	}, [dispatch])

	useEffect(() => {
		fetchReportStats()
	}, [fetchReportStats])

	const handleCardChartClick = route => {
		history.push(route)
	}

	const formatPercentPointDelta = n => {
		const s = formatDecimal(n, 2)
		if (n > 0) {
			return `+${s}`
		}
		return s
	}

	const handleNotifyAllUsers = () => {
		setIsReloadLoading(true)
		dispatch(
			onSendNotifications({}, () => {
				setIsReloadLoading(false)
			})
		)
	}

	return (
		<ListPageShell>
			<div className="dashboard-page">
				<ListPageIntro
					icon={HomeOutlined}
					title="Dashboard"
					subtitle="Quick view of users, feedback, saved words, and how missing definitions are trending versus last month."
				/>

				<div className="dashboard-page__grid">
					<CardChart
						chartID="dashboard-2"
						number={user ? user.total : 0}
						title="Registered users"
						color="warning"
						route={`${ROUTE.USER_MANAGEMENT}${ROUTE.LIST}`}
						onClick={handleCardChartClick}
					/>
					<CardChart
						chartID="dashboard-1"
						number={feedback ? feedback.total : 0}
						title="Feedback messages"
						route={`${ROUTE.FEEDBACK_MANAGEMENT}${ROUTE.LIST}`}
						onClick={handleCardChartClick}
					/>
					<CardChart
						chartID="dashboard-3"
						number={word ? word.total : 0}
						title="Saved words"
						color="danger"
						route={`${ROUTE.WORD_MANAGEMENT}${ROUTE.LIST}`}
						onClick={handleCardChartClick}
					/>
					<CardChart
						chartID="dashboard-4"
						number={
							statistic
								? statistic.percentWordDontHaveDefinition
										.deltaPercentPoints
								: 0
						}
						formatNumberFn={formatPercentPointDelta}
						title="Words missing definitions (change vs last month)"
						color="dark"
						route={`${ROUTE.FEEDBACK_MANAGEMENT}${ROUTE.REPORT}`}
						onClick={handleCardChartClick}
					/>
				</div>

				<Card size="small" bordered className="dashboard-page__report-lang-card">
					<Text type="secondary" className="dashboard-page__report-lang-intro">
						Same breakdown as on the user reports page: open{' '}
						<code>no_definition</code> vs resolved / definition-added
						feedback. Vietnamese chart and date filters are on{' '}
						<Button
							type="link"
							className="dashboard-page__report-lang-link"
							onClick={() =>
								history.push(
									`${ROUTE.FEEDBACK_MANAGEMENT}${ROUTE.REPORT}`
								)
							}
						>
							Feedback → User reports
						</Button>
						.
					</Text>
					<ReportWordLangPieCharts
						stats={reportStats}
						loading={reportStatsLoading}
						englishOnly
					/>
				</Card>

				<div className="dashboard-page__actions">
					<Popconfirm
						title="Notify every user?"
						description="This sends a notification to all accounts. Only use when you intend to reach everyone."
						onConfirm={handleNotifyAllUsers}
						okText="Send notification"
						cancelText="Cancel"
						okButtonProps={{ danger: true }}
					>
						<Button
							danger
							type="primary"
							loading={isReloadLoading}
							disabled={isReloadLoading}
							icon={<ReloadOutlined />}
						>
							Notify all users
						</Button>
					</Popconfirm>
				</div>
			</div>
		</ListPageShell>
	)
}
